import { type Task, TaskGroupBy } from "@common/types/task";
import { TaskColumn } from "@common/types/views/tasksView";
import type { BoardSortHint } from "@design-system/Board/Board.types";
import { BOARD_PLACE_KEY_LABEL } from "@design-system/Board/boardSensors";
import type { TaskBoardLaneBy } from "./taskBoardLanes";
import type { TaskBoardSort } from "./taskBoardSort";

/** The fields a card placed by hand can copy from its neighbour. */
export type TaskPlaceValues = Partial<Pick<Task, "dueDate" | "priority">>;

/** How a sort takes a card placed by hand between two others. */
interface TaskSortPlaceRule {
    /** The grouping that sets the same field, so a column or lane by it wins over placing. */
    groupBy: TaskGroupBy;
    /** The sort hint's second line: what holding the place key updates. */
    placeLabel: string;
    /** The sort field's value the placed card takes from its neighbour. */
    copy: (neighbour: Task) => TaskPlaceValues;
}

/**
 * Sorts a card can be placed in by hand, by board sort id. The placed card
 * copies its neighbour's value for the sort field, which keeps it there.
 */
const TASK_SORT_PLACE_RULES: Partial<Record<TaskBoardSort, TaskSortPlaceRule>> =
    {
        [TaskColumn.DUE_DATE]: {
            groupBy: TaskGroupBy.DUE_DATE,
            placeLabel: `Hold ${BOARD_PLACE_KEY_LABEL} to also update due date`,
            copy: ({ dueDate }) => ({ dueDate }),
        },
        [TaskColumn.PRIORITY]: {
            groupBy: TaskGroupBy.PRIORITY,
            placeLabel: `Hold ${BOARD_PLACE_KEY_LABEL} to also update priority`,
            copy: ({ priority }) => ({ priority }),
        },
    };

/** What the board's columns and lanes show. */
interface TaskPlaceAxes {
    groupBy: TaskGroupBy;
    laneBy: TaskBoardLaneBy | null;
}

interface TaskPlaceSort {
    sort: TaskBoardSort;
    axes: TaskPlaceAxes;
}

/** The sort's place rule, unless the columns or lanes already set its field. */
const findPlaceRule = ({ sort, axes }: TaskPlaceSort) => {
    const rule = TASK_SORT_PLACE_RULES[sort];
    return rule === undefined ||
        rule.groupBy === axes.groupBy ||
        rule.groupBy === axes.laneBy
        ? null
        : rule;
};

/** Whether the board offers placing a card between two others under `sort`. */
export const canPlaceTaskBetween = (placeSort: TaskPlaceSort) =>
    findPlaceRule(placeSort) !== null;

/** What a sorted board says over a column: the order, and how to place by hand where it can. */
export const getTaskBoardSortHint = ({
    sortLabel,
    ...placeSort
}: TaskPlaceSort & { sortLabel: string }): BoardSortHint => {
    const placeLabel = findPlaceRule(placeSort)?.placeLabel;
    return {
        title: `Board ordered by ${sortLabel}`,
        ...(placeLabel !== undefined && { placeLabel }),
    };
};

/** The board task with `id`, as a drop or a `+` names its neighbours; null past either end. */
export const findTask = (tasks: readonly Task[], id: string | null) =>
    tasks.find((task) => task.id === id) ?? null;

interface TaskPlaceSpot extends TaskPlaceSort {
    before: Task | null;
    after: Task | null;
}

/**
 * What a card placed by hand, or created at a spot, copies from its
 * neighbour: the card above's (else the one below's) value for the sort
 * field. Empty when there is neither or the sort takes no placed cards.
 */
export const getTaskPlaceValues = ({
    before,
    after,
    ...placeSort
}: TaskPlaceSpot): TaskPlaceValues => {
    const rule = findPlaceRule(placeSort);
    const neighbour = before ?? after;
    return rule === null || neighbour === null ? {} : rule.copy(neighbour);
};
